import React from 'react';
import { useNavigate } from 'react-router-dom';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import QuickButton from 'Douleutaras/components/QuickButton/QuickButton';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import { Trans } from 'react-i18next';
import Modal from 'Douleutaras/components/Modal/Modal';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import { useExtractSellerInfoFromJob } from 'Douleutaras/hooks/bids/pros';
import { CCFModalOptions } from 'Douleutaras/types/index';
import { AllModalOptions, ExtraBidModalOptions } from 'Douleutaras/constants/ccf/need-help-options';
import { ExtraBidSource, useAB } from 'Douleutaras/app/lib/ab';
import { useJobExtraBidState } from 'Douleutaras/hooks/jobs/jobExtraBidState';

import { requestCcfExtraBid } from './lib/actions';

interface ExtraBidModalProps {
  isOpen: boolean;
  title: string | React.ReactElement;
  options: CCFModalOptions;
  note: string | React.ReactElement;
  jobId: string;
  sellerId: string;
  userMugshot?: string;
  onCCFModalOptionSelected?({ optionSlug }: { optionSlug: AllModalOptions }): void;
  onExtraBidOptionSelected({ extraBidConversationId, extraBidSellerPhoneNumber, extraBidSellerName, extraBidSellerId }:
                     { extraBidConversationId: string,
                       extraBidSellerPhoneNumber: string,
                       extraBidSellerName: string,
                       extraBidSellerId: string,
                       optionSlug: AllModalOptions
                     }): void;
  onBack(): void;
  setIsOpen(modalStatus): void;
}

export const ExtraBidModal = ({
  isOpen,
  setIsOpen,
  title,
  options,
  note,
  jobId,
  sellerId,
  userMugshot,
  onCCFModalOptionSelected,
  onExtraBidOptionSelected,
  onBack
}: ExtraBidModalProps) => {
  const navigate = useNavigate();
  const { captureExtraBid } = useAB();
  // Get Pros who are related to bids of the jobId
  const allJobSellerData = useExtractSellerInfoFromJob({ jobId });
  const { bidsCount } = useJobExtraBidState(jobId);
  const filteredJobPros = allJobSellerData.filter(sellerData => String(sellerData.user.id) !== sellerId);

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>
      <DefaultModal
        serviceIcon={PopupServiceImg}
        serviceIconAlt={<Trans>T.CCF.EXTRA BID IMAGE ALT TEXT</Trans>}
        title={title}
        note={note}
        backText={<Trans>T.CCF.EXTRA BID BACK BUTTON TEXT</Trans>}
        backTextAriaLabel={<Trans>T.CCF.EXTRA BID BACK BUTTON TEXT</Trans>}
        closeAriaLabel={<Trans>T.CCF.EXTRA BID CLOSE ICON LABEL</Trans>}
        closeButtonCallback={() => setIsOpen(false)}
        backButtonCallback={typeof onBack === 'function' ? onBack : () => false}
        userMugshot={userMugshot}
      >
        <div>
          { options?.[ExtraBidModalOptions.I_WILL_PROCEED_WITH_A_PRO]?.active
            && filteredJobPros?.length > 0 && filteredJobPros.map(seller => {
              const sellerName = seller.user.attributes.sellerDisplayName;

              return (
                <ModalSelectionItem key={`key_seller_${seller.user.id}`}>
                  <QuickButton
                    preview
                    username={seller.user.attributes.username}
                    mugshot={seller.user.attributes.mugshot}
                    title={<Trans values={{ sellerName }}>T.CCF.CONTACT SELLER {{ sellerName }}</Trans>}
                    onClick={() => onExtraBidOptionSelected({
                      extraBidConversationId: seller.conversationId,
                      extraBidSellerPhoneNumber: seller.user.attributes.phone,
                      extraBidSellerName: seller.user.attributes.sellerDisplayName,
                      extraBidSellerId: seller.user.id,
                      optionSlug: ExtraBidModalOptions.I_WILL_PROCEED_WITH_A_PRO
                    })}
                  />
                </ModalSelectionItem>
              );
            })
          }
          { options?.[ExtraBidModalOptions.I_NEED_AN_EXTRA_PRO]?.active &&
            <ModalSelectionItem>
              <QuickButton
                preview
                disabled={options?.[ExtraBidModalOptions.I_NEED_AN_EXTRA_PRO]?.disabled}
                title={<Trans>T.CCF.REQUEST EXTRA BID</Trans>}
                tooltip={
                  options?.[ExtraBidModalOptions.I_NEED_AN_EXTRA_PRO]?.disabled
                    ? <Trans>T.JOB-PAGE.ADD-A-PROFESSIONAL-TOOLTIP</Trans>
                    : undefined
                }
                onClick={() => {
                  // @AB PostHog event
                  requestCcfExtraBid({
                    jobId,
                    bidsCount,
                    navigate,
                    onOptionSelected: onCCFModalOptionSelected,
                    eventTracking: captureExtraBid,
                    eventValue: ExtraBidSource.CCF_NEED_HELP
                  });
                }}
              />
            </ModalSelectionItem>
          }
        </div>
      </DefaultModal>
    </Modal>
  );
};

export default ExtraBidModal;
